import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText, Trash2, Video, Share2, Check, X } from "lucide-react";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { ProfessionPicker } from "@/components/ProfessionPicker";

function ChecklistRow({ done, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {done ? <Check className="w-4 h-4 text-[#2E7D5B]" /> : <X className="w-4 h-4 text-[#D96C5B]" />}
      <span className={done ? "text-[#1A1E1C]" : "text-[#68706B]"}>{label}</span>
    </div>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [videos, setVideos] = useState([]);
  const [bio, setBio] = useState(user?.bio || "");
  const [name, setName] = useState(user?.name || "");
  const [location, setLocation] = useState(user?.location || "");
  const [sector, setSector] = useState(user?.sector || "");
  const [professions, setProfessions] = useState(user?.professions || []);
  const [saving, setSaving] = useState(false);

  const loadVideos = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/videos", { params: { user_id: user.user_id } });
      setVideos(data);
    } catch (err) { console.warn("Failed to load videos", err); }
  };

  useEffect(() => { loadVideos(); /* eslint-disable-next-line */ }, [user?.user_id]);
  useEffect(() => {
    if (user) {
      setBio(user.bio || ""); setName(user.name || "");
      setLocation(user.location || ""); setSector(user.sector || "");
      setProfessions(user.professions || []);
    }
  }, [user]);

  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", { name, bio, location, sector, professions });
      await refresh();
      toast.success("Profile updated");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const uploadResume = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post("/users/me/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Resume uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Avatar updated");
    } catch { toast.error("Failed"); }
  };

  const deleteVideo = async (id) => {
    if (!confirm("Delete this video?")) return;
    try {
      await api.delete(`/videos/${id}`);
      setVideos((vs) => vs.filter((v) => v.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/p/${user.user_id}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Public profile link copied — share it anywhere"),
      () => toast.message(url)
    );
  };

  const avatarSrc = user.avatar?.startsWith("/api")
    ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar}`
    : user.avatar;

  const hasApprovedVideo = videos.some((v) => v.moderation_status === "approved");

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="grid md:grid-cols-12 gap-6">
        <aside className="md:col-span-4 bg-white border border-[#E2E4E2] p-6">
          <label className="block cursor-pointer">
            <div className="w-28 h-28 mx-auto bg-[#E8ECEB] overflow-hidden flex items-center justify-center text-3xl font-bold text-[#1E3F33]">
              {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-input" />
            <div className="text-center text-xs text-[#68706B] mt-2 underline">Change photo</div>
          </label>
          <div className="mt-6 text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D96C5B] font-semibold">{user.role}</div>
            <div className="text-lg font-bold mt-1 flex items-center justify-center gap-1.5">
              {user.name} {user.is_verified && <VerifiedBadge className="w-5 h-5" />}
            </div>
            <div className="text-xs text-[#68706B]">{user.email}</div>
          </div>

          {user.role === "candidate" && (
            <Button onClick={copyShareLink} variant="outline" className="w-full rounded-none mt-4 border-[#1E3F33] text-[#1E3F33]" data-testid="share-profile-btn">
              <Share2 className="w-4 h-4 mr-2" /> Copy public profile link
            </Button>
          )}

          {user.role === "candidate" && !user.is_verified && (
            <div className="mt-5 bg-[#F6F5F0] border border-[#E2E4E2] p-4" data-testid="verified-checklist">
              <div className="text-xs uppercase tracking-[0.2em] text-[#68706B] font-semibold mb-2">Get verified</div>
              <div className="space-y-1.5">
                <ChecklistRow done={!!user.avatar} label="Upload a real photo" />
                <ChecklistRow done={!!user.resume_file_id} label="Upload your resume PDF" />
                <ChecklistRow done={hasApprovedVideo} label="Get 1 video approved" />
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#68706B] font-semibold mb-2">Resume PDF</div>
              {user.resume_file_id ? (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${user.resume_file_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-[#F0EFEB] px-3 py-2 hover:bg-[#E8ECEB]" data-testid="resume-link">
                  <FileText className="w-4 h-4 text-[#1E3F33]" /> <span className="text-sm">View current resume</span>
                </a>
              ) : (
                <div className="text-xs text-[#68706B]">No resume uploaded yet.</div>
              )}
              <input id="resume-file-input" type="file" accept="application/pdf" className="hidden" onChange={uploadResume} data-testid="resume-input" />
              <label htmlFor="resume-file-input" className="block cursor-pointer">
                <span className="inline-flex items-center justify-center w-full h-10 mt-2 border border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8ECEB] text-sm font-medium">Upload PDF</span>
              </label>
            </label>
          </div>
        </aside>

        <section className="md:col-span-8 space-y-6">
          <div className="bg-white border border-[#E2E4E2] p-6">
            <h2 className="text-xl font-bold tracking-tight">Profile</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded-none h-11" data-testid="profile-name" />
              <Input value={sector} onChange={(e) => setSector(e.target.value)} placeholder="Sector" className="rounded-none h-11" data-testid="profile-sector" />
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className="rounded-none h-11 md:col-span-2" data-testid="profile-location" />
              <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Short bio" className="rounded-none min-h-24 md:col-span-2" data-testid="profile-bio" />
            </div>
            {user.role === "candidate" && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#68706B] font-semibold mb-2">My professions</div>
                <ProfessionPicker value={professions} onChange={setProfessions} />
              </div>
            )}
            <Button onClick={save} disabled={saving} className="rounded-none mt-4 bg-[#1E3F33] hover:bg-[#152C24]" data-testid="profile-save">{saving ? "Saving…" : "Save changes"}</Button>
          </div>

          {user.role === "candidate" && (
            <div className="bg-white border border-[#E2E4E2] p-6">
              <h2 className="text-xl font-bold tracking-tight flex items-center gap-2"><Video className="w-5 h-5" /> My visual resumes</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                {videos.length === 0 && <div className="text-sm text-[#68706B] col-span-full">Nothing uploaded yet.</div>}
                {videos.map((v) => (
                  <div key={v.id} data-testid={`my-video-${v.id}`}>
                    <div className="relative aspect-[3/4] bg-black overflow-hidden group">
                      <video src={fileUrl(v.video_file_id)} poster={v.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${v.thumbnail_url}` : undefined} className="absolute inset-0 w-full h-full object-cover" muted />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent" />
                      <div className="absolute bottom-2 left-2 right-2 text-white text-xs font-semibold line-clamp-2">{v.title}</div>
                      {v.moderation_status !== "approved" && (
                        <div className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-[#D96C5B] text-white px-1.5 py-0.5">{v.moderation_status}</div>
                      )}
                      <button onClick={() => deleteVideo(v.id)} className="absolute top-2 right-2 bg-black/60 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-video-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {v.moderation_status === "rejected" && v.moderation_reason && (
                      <div className="text-xs text-[#D96C5B] mt-1.5 leading-snug" data-testid={`reject-reason-${v.id}`}>
                        Rejected: {v.moderation_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
